import React, { useEffect, useState } from "react";
import Loader from "./Common/Loader";
import "../styles/myplan.css";
import moment from "moment";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { DataSeriesTypes } from "../types";
// import { errornotify } from "./Message";
import { NoResult } from "./Common/NoResult";
import { useSelector } from "react-redux";
import {
  exportUSNPlanList,
  fetchUSNPlanDetail,
  fetchUSNPlanList,
} from "../api/usnPlan";
import { getLogoSrc } from "../helpers/func";
import Datepicker from "./Datepicker";
import { selectUtility } from "../store/utility/selector";
import { RankChart } from "./Charts/RankChart";
import { Tooltip, Typography } from "@mui/material";
import { MultiSelectFilterDropdown } from "./Common/MultiSelectFilterDropdown";
import { usePersistedPagination } from "./Hooks/usePersistedPagination";
import { useTableSortReset } from "./Hooks/useTableSortReset";
import {
  extractUniqueTerms,
  matchesTermFilter,
} from "../utils/planFilters";
import {
  createNumericSortFunction,
  createStringSortFunction,
} from "../utils/planTableSort";
import {
  ActiveFilterTags,
  activeFilterTagClassName,
} from "./Common/ActiveFilterTags";

interface planObj {
  cancel_fee: string;
  created_ts: string;
  diff: string;
  efl_url: string;
  offline_ts: string;
  pd_id: string;
  plan_id: string;
  plan_name: string;
  price500: string;
  price1000: string;
  price2000: string;
  provider_id: string;
  provider_name: string;
  source: string;
  term: string;
  utility_id: string;
  utility_name: string;
  contract_term: string;
  rate: string;
  documents: any;
}

interface detail {
  plan: any;
}

const today = moment().format("YYYY-MM-DD");

const calDate = (val: string) => {
  return moment(val, "YYYY-MM-DD").subtract(30, "days").format("YYYY-MM-DD");
};

const USN_MyPlansList = () => {
  const currentLocation = useSelector<
    any,
    { state_id: string; state_name: string }
  >((state) => state.Location.currentLocation);
  const [isLoading, setIsLoading] = useState(false);
  const [filterValue, setFilterValue] = useState({
    created_ts: today,
  });
  const [dataPlanList, setDataPlanList] = useState<Array<planObj>>([]);
  const [selectedTerms, setSelectedTerms] = useState<string[]>([]);
  const [openSelect, setOpenSelect] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const {
    tablePage,
    setTablePage,
    rowsPerPage,
    handleRowsPerPageChange,
    resetPage,
    paginationRowsPerPageOptions,
  } = usePersistedPagination("usnMyPlans_rowsPerPage", 20);
  const currentUtility = useSelector(selectUtility);

  const getPlanList = async (val: any) => {
    setIsLoading(true);
    setDataPlanList([]);
    resetPage();
    const res = await fetchUSNPlanList(
      currentLocation?.state_id,
      val.created_ts,
      currentUtility.utility_id
    );
    if (res.status !== 1 || res.response.length === 0) {
      return setIsLoading(false);
    }
    setDataPlanList(res.response);
    setIsLoading(false);
  };

  useEffect(() => {
    if (!currentUtility) return;

    getPlanList(filterValue);
  }, [filterValue, currentUtility]);

  const changeDate = (val: any) => {
    setFilterValue({
      ...filterValue,
      created_ts: moment(val).format("YYYY-MM-DD"),
    });
  };

  let filterData = dataPlanList.filter((item) =>
    matchesTermFilter(item, selectedTerms, "contract_term")
  );
  const filterTerms = extractUniqueTerms(dataPlanList, "contract_term");

  const renderTopPlanTable = () => {
    const columns: any[] = [
      {
        name: <span className="font-weight-bold fs-14">REP</span>,
        selector: (row: planObj) => {
          return (
            <img
              src={getLogoSrc(row.provider_id, currentLocation?.state_id)}
              alt=""
              height={40}
            />
          );
        },
        sortable: true,
        sortFunction: createStringSortFunction("provider_name"),
      },
      {
        name: <span className="font-weight-bold fs-14">Plan Name</span>,
        selector: (row: planObj) => {
          return (
            <Tooltip 
              title={row.plan_name} 
              placement="top"
              id={row.plan_id}
              arrow
            >
              <div className="plan-name-cell">
                <span className="text-truncate d-inline-block font-weight-bold fs-14">
                  {row.plan_name}
                </span>
              </div>
            </Tooltip>
          );
        },
        sortable: true,
        sortFunction: createStringSortFunction("plan_name"),
      },
      {
        name: <span className="font-weight-bold fs-14">Term</span>,
        selector: (row: planObj) => row.contract_term,
        sortable: true,
        sortFunction: createNumericSortFunction("contract_term"),
        style: {
          paddingLeft: "1.5rem",
        },
        width: "95px",
      },
      {
        name: <span className="font-weight-bold fs-14">Rate</span>,
        selector: (row: planObj) => row.rate,
        cell: (row: planObj) => `${row.rate}¢`,
        sortable: true,
        sortFunction: createNumericSortFunction("rate"),
      },
      {
        name: (
          <span className="font-weight-bold fs-14 text-nowrap">
            Cancellation Fee
          </span>
        ),
        selector: (row: planObj) => row.cancel_fee,
        sortable: true,
        sortFunction: createNumericSortFunction("cancel_fee"),
        width: "148px",
        minWidth: "148px",
      },
      {
        name: <span className="font-weight-bold fs-14">Source</span>,
        selector: (row: planObj) => row.source,
        sortable: true,
        sortFunction: createStringSortFunction("source"),
      },
      {
        name: <span className="font-weight-bold fs-14"></span>,
        selector: (row: planObj): any => {
          const findEfl = row?.documents?.find((item: any) =>
            item?.description?.toLowerCase().includes("efl")
          );

          return (
            <div className="d-flex flex-wrap gap-2">
              <Button
                color="secondary"
                outline
                className="btn btn-sm"
                onClick={() => handleOpen(row.plan_id, row.pd_id, row.contract_term)}
              >
                Snapshot
              </Button>
              {findEfl && (
                <Button
                  onClick={() => {
                    window.open(findEfl.location, "_blank", "noreferrer");
                  }}
                  outline
                  className="btn btn-sm"
                >
                  EFL
                </Button>
              )}
            </div>
          );
        },
      },
    ];
    const customStyles = {
      headCells: {
        style: {
          backgroundColor: "#F2F2F2",
          paddingRight: 0,
        },
      },
    };

    return (
      <DataTable
        columns={columns}
        data={filterData}
        paginationPerPage={rowsPerPage}
        paginationRowsPerPageOptions={paginationRowsPerPageOptions}
        onChangeRowsPerPage={handleRowsPerPageChange}
        customStyles={customStyles}
        pagination
        paginationDefaultPage={tablePage}
        onChangePage={(page) => setTablePage(page)}
      />
    );
  };

  const [open, setOpen] = React.useState(false);
  const [isShow, setIsShow] = useState(false);
  const [planDetail, setPlanDetail] = useState<detail>({
    plan: {},
  });
  const [detailData, setDetailData] = useState<DataSeriesTypes[]>([]);
  const [findDetailData, setFindDetailData] = useState(true);

  const handleOpen = async (plan_id: any, pd_id: any, contractTerm: string) => {
    setOpen(true);
    setIsShow(true);
    const res = await fetchUSNPlanDetail(
      plan_id,
      pd_id,
      `${calDate(filterValue.created_ts)}&${filterValue.created_ts}`,
      contractTerm
    );

    const resData: any = () => {
      if (res.status === 1) {
        return res.response;
      } else {
        setFindDetailData(false);
        setIsShow(false);
        // errornotify(res.message);
        return;
      }
    };

    setFindDetailData(true);

    const series: any[] = [];
    resData()?.rank_trend.forEach((item: any) => {
      series.push({ x: item.date, y: item.rank || null, detail: item });
    });

    setDetailData([{ id: resData()?.plan?.provider_name, data: series }]);

    setPlanDetail({
      plan: resData()?.plan || {},
    });
    setIsShow(false);
  };

  const handleClose = () => setOpen(false);

  const filterSelections = () => {
    let list: React.ReactNode[] = [],
      key = 0;
    selectedTerms.forEach((term) => {
      list.push(
        <button
          key={key++}
          className={activeFilterTagClassName}
          onClick={() =>
            setSelectedTerms((prev) => prev.filter((item) => item !== term))
          }
        >
          {`Term: ${term} Month`}
          <i className="bx bx-x fs-15 ms-2" />
        </button>
      );
    });
    return <ActiveFilterTags tags={list} />;
  };

  const exportList = async () => {
    setIsDownload(true);
    const res = await exportUSNPlanList(
      currentLocation?.state_id,
      filterValue.created_ts,
      currentUtility.utility_id,
      "1"
    );

    const fileName = res.headers
      .get("content-disposition")
      ?.split("filename=")[1];

    const data = await res.blob();

    const url = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    link.style.display = "none";
    link.href = url;
    link.setAttribute(
      "download",
      fileName || `MyPlans_${filterValue.created_ts}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsDownload(false);
  };

  return (
    <>
      <Card style={{ minHeight: "30rem" }}>
        <CardHeader className="align-items-center justify-content-between d-flex">
          <div>
            <h4 className="card-title mb-0 flex-grow-1">Listing of Current In-Market Plans</h4>
            {currentLocation && currentUtility && (
              <Typography
                variant="subtitle2"
                className="text-muted"
              >{`${currentLocation.state_name} - ${currentUtility.utility_name}`}</Typography>
            )}
          </div>
          <div className="flex-shrink-0">
            {!isDownload ? (
              <Button
                color="secondary"
                onClick={exportList}
                className="btn-label"
              >
                <i className="bx bx-download label-icon align-middle fs-17 me-2"></i>
                <span>Download</span>
              </Button>
            ) : (
              <Button color="secondary" className="btn-load" outline>
                <span className="d-flex align-items-center">
                  <Spinner size="sm" className="flex-shrink-0">
                    {" "}
                    Loading...{" "}
                  </Spinner>
                  <span className="flex-grow-1 ms-2">Loading...</span>
                </span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
            <MultiSelectFilterDropdown
              isOpen={openSelect}
              toggle={() => setOpenSelect(!openSelect)}
              options={filterTerms}
              selected={selectedTerms}
              onChange={setSelectedTerms}
              allLabel="All Terms"
              formatOption={(value) => `${value} Month`}
              width={120}
            />
            <Datepicker
              handleChangeDate={(val: any) => changeDate(val)}
              disabled={isLoading}
            />
          </div>
          <div className="mb-3">{filterSelections()}</div>

          {isLoading ? (
            <Loader />
          ) : (
            <div className="rdt_out">
              {dataPlanList?.length > 0 ? renderTopPlanTable() : <NoResult />}
            </div>
          )}
        </CardBody>
      </Card>

      <Modal size="lg" isOpen={open} toggle={handleClose} centered>
        <ModalHeader toggle={handleClose}>
          My Plan Detail
          <span className="fs-14 text-muted ms-3">
            {calDate(filterValue.created_ts) || "-"} -{" "}
            {filterValue.created_ts || "-"}
          </span>
        </ModalHeader>
        {findDetailData ? (
          <div className="modal-body">
            {isShow ? (
              <Loader />
            ) : (
              <React.Fragment>
                <Table className="align-middle table-fixed mb-0">
                  <thead>
                    <tr>
                      <th scope="col">Provider Name</th>
                      <th scope="col">Utility Name</th>
                      <th scope="col">Term</th>
                      <th scope="col">Base Rate</th>
                      <th scope="col">Plan Source</th>
                      <th scope="col">Cancellation Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{planDetail.plan.provider_name}</td>
                      <td>{planDetail.plan.utility_name}</td>
                      <td>{planDetail.plan.contract_term}</td>
                      <td>
                        {planDetail.plan.rate}
                        {planDetail.plan.rate ? "¢" : ""}
                      </td>
                      <td>{planDetail.plan.source || ""}</td>
                      <td>{planDetail.plan.cancel_fee}</td>
                    </tr>
                  </tbody>
                </Table>
                <RankChart
                  title="Rank Trend"
                  data={detailData}
                  isLoading={isLoading}
                />
              </React.Fragment>
            )}
          </div>
        ) : (
          <NoResult />
        )}
      </Modal>
    </>
  );
};

export default USN_MyPlansList;
